// Shop members / permissions management (PERMISSIONS REVAMP 2026-08-01).
// Reads/writes the `users` collection — the source the Firestore rules gate on
// (role + flat `permissions` map). Owners manage roles & à-la-carte toggles here.
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { StaffRole } from '../types/staff';
import { isOwnerRole } from '../shared/authority';

// Roles that are "shop staff" (customers excluded from the member list).
export const STAFF_ROLES: StaffRole[] = ['owner', 'manager', 'barber', 'trainee', 'shop_attendant', 'accountant'];

// The à-la-carte permission flags the rules honor via hasPerm().
export const PERMISSION_KEYS = [
  'editSettings', 'manageStaff', 'assignRoles', 'viewReports', 'viewPayroll',
  'manageClients', 'processPayments', 'chargePayments', 'manageInventory',
  'canDiscount', 'canEmergencyBook', 'refundVoid',
] as const;
export type PermissionKey = typeof PERMISSION_KEYS[number];

export const PERMISSION_LABELS: Record<PermissionKey, string> = {
  editSettings: 'Edit Settings',
  manageStaff: 'Manage Staff',
  assignRoles: 'Assign Roles',
  viewReports: 'View Shop Reports',
  viewPayroll: 'View Payroll',
  manageClients: 'Manage Clients',
  processPayments: 'Take Payments',
  chargePayments: 'Charge Money (off = no-charge)',
  manageInventory: 'Manage Inventory',
  canDiscount: 'Give Discounts',
  canEmergencyBook: 'Emergency Booking',
  refundVoid: 'Refund / Void Sales',
};

// Sensible defaults per role (owner can override any per-member).
export function defaultPermissionsFor(role: StaffRole): Record<PermissionKey, boolean> {
  const all = (v: boolean) => PERMISSION_KEYS.reduce((o, k) => ({ ...o, [k]: v }), {} as Record<PermissionKey, boolean>);
  switch (role) {
    case 'owner': return all(true);
    case 'manager': return { ...all(true), assignRoles: false };
    case 'barber': return { ...all(false), processPayments: true, chargePayments: true, viewReports: false };
    case 'trainee': return { ...all(false), processPayments: true, chargePayments: false }; // no-charge checkout
    case 'shop_attendant': return { ...all(false), processPayments: true, chargePayments: true, manageClients: true };
    case 'accountant': return { ...all(false), viewReports: true, viewPayroll: true };
    default: return all(false);
  }
}

export interface ShopMember {
  uid: string;
  email: string;
  name: string;
  role: StaffRole;
  permissions: Partial<Record<PermissionKey, boolean>>;
  isOwner: boolean;
}

class ShopMembersService {
  /** List every staff-type member (excludes customers). */
  async listMembers(): Promise<ShopMember[]> {
    const snap = await getDocs(collection(db, 'users'));
    const members: ShopMember[] = [];
    snap.forEach(d => {
      const u = d.data();
      if (STAFF_ROLES.includes(u.role)) {
        members.push({
          uid: d.id,
          email: u.email || '(no email)',
          name: u.name || u.displayName || '',
          role: u.role,
          permissions: u.permissions || {},
          isOwner: isOwnerRole(u.role),
        });
      }
    });
    // Owners first, then by role order.
    return members.sort((a, b) => STAFF_ROLES.indexOf(a.role) - STAFF_ROLES.indexOf(b.role));
  }

  /** Update a member's role + flat permissions map (what the rules read). */
  async updateMember(uid: string, role: StaffRole, permissions: Record<PermissionKey, boolean>): Promise<void> {
    await updateDoc(doc(db, 'users', uid), {
      role,
      permissions,
      isOwner: isOwnerRole(role),
      updatedAt: new Date(),
    });
  }
}

export const shopMembersService = new ShopMembersService();
